import {
  guidelinesHttp,
  FolderDto,
  FolderCreateDto,
  FolderUpdateDto,
} from '@/lib/api/guidelines-client';
import { FoldersService } from '@inference.engine/guidelines-api-client';

/**
 * Exports all guidelines in a folder as a text file
 * @param folderId - The folder ID to export
 * @returns Blob and filename from the Content-Disposition header
 */
export const exportFolderGuidelines = async (
  folderId: string,
): Promise<{ blob: Blob; fileName: string }> => {
  const response = await FoldersService.exportGuidelines({
    client: guidelinesHttp,
    path: { folderId },
    responseType: 'blob',
  });

  const contentDisposition =
    'headers' in response
      ? (response.headers['content-disposition'] as string | undefined)
      : undefined;
  const fileNameMatch = contentDisposition?.match(/filename="(.+)"/);
  const fileName = fileNameMatch?.[1] ?? 'guidelines.txt';

  return { blob: response.data as Blob, fileName };
};

/**
 * Fetches all folders for a given workspace
 * @param workspaceId - The workspace ID to fetch folders for
 * @returns Array of FolderDto objects
 */
export const getFoldersByWorkspace = async (workspaceId: string): Promise<FolderDto[]> => {
  const response = await FoldersService.getFoldersByWorkspace({
    client: guidelinesHttp,
    query: { workspaceId },
  });
  return response.data ?? [];
};

/**
 * Creates a new folder
 * @param data - The folder data to create
 * @returns The created FolderDto
 */
export const createFolder = async (data: FolderCreateDto): Promise<FolderDto | null> => {
  const response = await FoldersService.createFolder({
    client: guidelinesHttp,
    body: data,
  });
  return response.data ?? null;
};

/**
 * Updates an existing folder
 * @param folderId - The folder ID to update
 * @param data - The updated folder data
 * @returns The updated FolderDto
 */
export const updateFolder = async (
  folderId: string,
  data: FolderUpdateDto,
): Promise<FolderDto | null> => {
  const response = await FoldersService.updateFolder({
    client: guidelinesHttp,
    path: { folderId },
    body: data,
  });
  return response.data ?? null;
};

/**
 * Deletes a folder
 * @param folderId - The folder ID to delete
 */
export const deleteFolder = async (folderId: string): Promise<void> => {
  await FoldersService.deleteFolder({
    client: guidelinesHttp,
    path: { folderId },
  });
};
